import { createMachine, assign, spawn, sendTo, interpret } from "xstate";
import * as HorizonStates from "../constants/horizonStates";
import * as HorizonEvents from "../constants/horizonEvents";
import * as HorizonErrors from "../constants/horizonErrors";
import * as WidgetEvents from "../constants/widgetEvents";
import * as DataLoadingStates from "../constants/dataLoadingStates";
import * as horizonAccessorMachine from "./horizonAccessorMachine";
import * as LoggingUtils from "./utils/logging";
import widgetStateMachine from "./widgetStateMachine";

const debug = process.env.NODE_ENV !== "production";

const getId = () => {
    return (
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15)
    );
};

const horizonStateMachine = createMachine(
    {
        id: `horizon`,
        context: {
            debug: {
                isDebug: debug,
            },
            hasMounted: false,
            horizonData: null,
            machines: [],
            queue: [],
            roleRights: {},

            signCollections: null,
            widgets: {},

            occupancySettingsLoadState: DataLoadingStates.NOT_REQUESTED,
            occupancySettings: null,
        },
        initial: HorizonStates.PLATFORM_INITIALISING,
        on: {
            MACHINE_KILL: {
                actions: [
                    (context, event) => {
                        if (debug) {
                            LoggingUtils.default.logMachineEvent(
                                "HORIZON",
                                "MACHINE_KILL",
                                event.id
                            );
                        }
                    },
                    assign({
                        machines: (context, event) => {
                            return context.machines.filter((x) => {
                                return x.id !== event.id;
                            });
                        },
                    }),
                ],
            },
            HAS_MOUNTED: {
                actions: assign({
                    hasMounted: true,
                }),
            },
            UPDATE_DATA: {
                actions: assign({
                    horizonData: (context, event) => ({
                        ...event.horizonData,
                        ...context.horizonData,
                    }),
                }),
            },
            QUEUE_ADD: {
                actions: assign({
                    queue: (context, event) => {
                        const updateQueue = context.queue;
                        updateQueue.push({
                            ...event.task,
                            id: getId(),
                        });
                        return updateQueue;
                    },
                }),
            },
            QUEUE_TASK_COMPLETE: {
                actions: assign({
                    queue: (context, event) => {
                        return context.queue.filter(
                            (task) => task.id !== event.taskId
                        );
                    },
                }),
            },
            WIDGET_SEND_TO: [
                {
                    actions: [
                        (context, event) => {
                            if (debug) {
                                const eventName =
                                    typeof event.eventData === "object" &&
                                    event.eventData !== null
                                        ? event.eventData.type
                                        : event.eventData;
                                LoggingUtils.default.logSendToChild(
                                    "HORIZON",
                                    event.widgetId,
                                    "",
                                    eventName
                                );
                            }
                        },
                        sendTo(
                            (context, event) => context.widgets[event.widgetId],
                            (context, event) => event.eventData
                        ),
                    ],
                },
            ],
            ROLE_RIGHT_GET: [
                {
                    cond: "shouldLoadRoleRight",
                    actions: [
                        // set placeholder to avoid multiple loads
                        assign({
                            roleRights: (context, event) => {
                                const roleRights = context.roleRights;
                                roleRights[event.roleRight] = null;
                                return roleRights;
                            },
                        }),
                        "initHorizonAccessorMachine",
                    ],
                },
            ],
            ROLE_RIGHT_SET: {
                actions: assign({
                    roleRights: (context, event) => {
                        const roleRights = context.roleRights;
                        roleRights[event.additionalData.roleRight] = event.data;
                        return roleRights;
                    },
                }),
            },
            USER_SETTINGS_RESET: {
                actions: "initHorizonAccessorMachine",
            },
            USER_SETTINGS_SAVE: {
                actions: "initHorizonAccessorMachine",
            },
            WIDGET_INSTANCE_SAVE: {
                actions: "initHorizonAccessorMachine",
            },
            HORIZON_ACCESSOR_ERRORED: [
                {
                    cond: "dataLoadRequestedByAWidget",
                    actions: [
                        sendTo(
                            (context, event) =>
                                context.widgets[event.additionalData.widgetId],
                            (context, event) => {
                                return {
                                    type: WidgetEvents.ERROR,
                                    ...event.additionalData,
                                    errorType:
                                        HorizonErrors.GETTING_DATA_FROM_PLATFORM,
                                };
                            }
                        ),
                    ],
                },
            ],
            SIGN_COLLECTIONS_GET: [
                {
                    actions: "initHorizonAccessorMachine",
                    cond: "shouldLoadSignCollections",
                },
            ],
            SIGN_COLLECTIONS_SET: {
                actions: assign({
                    signCollections: (context, event) => event.data,
                }),
            },
            OCCUPANCY_SETTINGS_GET: {
                actions: assign({
                    occupancySettingsLoadState: DataLoadingStates.LOAD,
                }),
                cond: "occupancySettingsNotLoaded",
            },
            OCCUPANCY_SETTINGS_SET_CONTEXT: {
                actions: assign({
                    occupancySettings: (context, event) =>
                        event.occupancySettings,
                    occupancySettingsLoadState: DataLoadingStates.LOADED,
                }),
            },
            OCCUPANCY_SETTINGS_SET_DB: {
                actions: assign({
                    occupancySettingsLoadState: DataLoadingStates.LOAD,
                }),
            },
            OCCUPANCY_SETTINGS_ERROR: {
                actions: assign({
                    occupancySettingsLoadState: DataLoadingStates.ERROR,
                }),
            },
            NO_FURTHER_ACTION: {}, // NULL state used so child machines can return a successful action
            LOG: {
                actions: "debugLog",
            },
        },
        states: {
            PLATFORM_INITIALISING: {
                on: {
                    HAS_MOUNTED: HorizonStates.PLATFORM_IDLE,
                },
            },
            PLATFORM_IDLE: {
                always: {
                    target: HorizonStates.QUEUE_RUN,
                    cond: "queueHasTasks",
                },
            },
            QUEUE_RUN: {
                always: {
                    target: HorizonStates.PLATFORM_IDLE,
                    cond: "queueIsEmpty",
                },
                on: {
                    WIDGET_ADD: {
                        cond: "doesWidgetNotExist",
                        actions: assign({
                            widgets: (context, event) => {
                                const loadedWidgets = context.widgets;
                                loadedWidgets[event.widgetId] = spawn(
                                    widgetStateMachine.widgetStateMachineConstructor(
                                        event.widgetId,
                                        event.widgetName,
                                        event.containerData
                                    ),
                                    { sync: true }
                                );
                                return loadedWidgets;
                            },
                        }),
                    },
                    POPUP_WIDGET_OPEN: {
                        actions: "showPopupWidget",
                    },
                    HELP_OPEN: {
                        actions: "showHelp",
                    },
                },
            },
        },
    },
    {
        guards: {
            hasInitialised: (context) => {
                return context.hasMounted && context.horizonData !== null;
            },
            queueHasTasks: (context) => {
                return context.queue.length > 0;
            },
            queueIsEmpty: (context) => {
                return context.queue.length === 0;
            },
            doesWidgetNotExist: (context, event) => {
                return !Object.hasOwn(context.widgets, event.widgetId);
            },
            shouldLoadRoleRight: (context, event) => {
                return !Object.hasOwnProperty.call(
                    context.roleRights,
                    event.roleRight
                );
            },
            occupancySettingsNotLoaded: (context) => {
                return (
                    context.occupancySettingsLoadState ===
                    DataLoadingStates.NOT_REQUESTED
                );
            },
            shouldLoadSignCollections: (context) => {
                return (
                    context.signCollections === null &&
                    !context.machines.some(
                        (x) =>
                            x.function ===
                            horizonAccessorMachine.functions.SIGN_COLLECTIONS
                    )
                );
            },
            dataLoadRequestedByAWidget: (context, event) => {
                return typeof event.additionalData.widgetId !== "undefined";
            },
        },
        actions: {
            showHelp: (context, event) => {
                WebHelp.show(event.helpUrl);
            },
            showPopupWidget: (context, event) => {
                PopupWidget.show(event.key);
            },
            debugLog: (context, event) => {
                if (!debug) {
                    return;
                }
                if (Object.prototype.hasOwnProperty.call(event.data, "text")) {
                    LoggingUtils.default.log(event.data.text, event.data.level);
                } else {
                    console.log(event.data);
                }
            },
            initHorizonAccessorMachine: assign({
                machines: (context, event) => {
                    const updateMachines = context.machines;
                    const id = getId();
                    let machineFunction = null;
                    const { type, ...additionalData } = event;
                    switch (event.type) {
                        case HorizonEvents.SIGN_COLLECTIONS_GET:
                            machineFunction =
                                horizonAccessorMachine.functions
                                    .SIGN_COLLECTIONS;
                            break;
                        case HorizonEvents.ROLE_RIGHT_GET:
                            machineFunction =
                                horizonAccessorMachine.functions.ROLE_RIGHTS;
                            break;
                        case HorizonEvents.USER_SETTINGS_SAVE:
                            machineFunction =
                                horizonAccessorMachine.functions
                                    .USER_SETTINGS_SAVE;
                            break;
                        case HorizonEvents.USER_SETTINGS_RESET:
                            machineFunction =
                                horizonAccessorMachine.functions
                                    .USER_SETTINGS_RESET;
                            break;
                        case HorizonEvents.WIDGET_INSTANCE_SAVE:
                            machineFunction =
                                horizonAccessorMachine.functions
                                    .WIDGET_INSTANCE_SAVE;
                            break;
                        default:
                            return context.machines;
                    }
                    updateMachines.push({
                        id,
                        function: machineFunction,
                        machine: spawn(
                            horizonAccessorMachine.horizonAccessorMachineConstructor(
                                id,
                                machineFunction,
                                additionalData
                            )
                        ),
                    });
                    return updateMachines;
                },
            }),
        },
    }
);

// eslint-disable-next-line import/prefer-default-export
export const horizonService = {
    service: interpret(horizonStateMachine),
    currentState: horizonStateMachine.initialState,
    context: horizonStateMachine.context,
    sendToWidget(widgetId, event) {
        this.service.send({
            type: HorizonEvents.WIDGET_SEND_TO,
            widgetId,
            eventData: event,
        });
    },
    checkRoleRight(roleRight, defaultValue = false) {
        const hasRoleRight = this.context.roleRights[roleRight];

        if (typeof hasRoleRight === "undefined") {
            this.service.send({
                type: HorizonEvents.ROLE_RIGHT_GET,
                roleRight,
            });
        }
        return typeof hasRoleRight === "boolean" ? hasRoleRight : defaultValue;
    },
};

const transitionLog = (newState) => {
    if (
        newState._event.name === "xstate.init" ||
        newState._event.name === "WIDGET_SEND_TO" ||
        (newState._event.name === "xstate.update" &&
            newState._event.data.state._event.name === "xstate.update")
    ) {
        return;
    }

    if (newState._event.name === "xstate.update") {
        LoggingUtils.default.logEventTransition(
            newState._event.data.state.context.widgetId,
            newState._event.data.state._event.name,
            typeof newState._event.data.state.history !== "undefined"
                ? newState._event.data.state.history.value
                : "-",
            newState._event.data.state.value
        );
    } else {
        LoggingUtils.default.logEventTransition(
            "HORIZON",
            newState._event.name,
            horizonService.currentState,
            newState.value
        );
    }
};

horizonService.service.onTransition((newState) => {
    if (debug) {
        transitionLog(newState);
    }
    horizonService.currentState = newState.value;
    horizonService.context = newState.context;
});
horizonService.service.start();
